import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowRightIcon, CalendarIcon, MapPinIcon } from 'lucide-react';
import { PageHero } from '../components/layout/PageHero';
import { SectionHeading } from '../components/ui/SectionHeading';
import { CtaBand } from '../components/ui/CtaBand';
import { NotFound } from './NotFound';
import { regions } from '../data/regions';
import { policies } from '../data/policies';
import { events } from '../data/events';
import { articles } from '../data/articles';
export function RegionDetail() {
  const {
    region: regionId = ''
  } = useParams();
  const region = regions.find(item => item.id === regionId);
  if (!region) {
    return <NotFound data-id="element-1286" data-mp="3fkssbd6ncxrm" />;
  }
  const relatedPolicies = policies.slice(0, 4);
  const localEvents = events.filter(event => region.states.includes(event.state));
  const localNews = articles.filter(article => article.tags.some(tag => tag === region.name || region.states.includes(tag)));
  return <>
      <PageHero eyebrow="Regional agenda" title={region.name} intro={region.headline} crumbs={[{
      label: 'Home',
      to: '/'
    }, {
      label: 'Nigeria',
      to: '/nigeria'
    }, {
      label: region.name
    }]} meta={<ul className="flex flex-wrap gap-2" data-id="element-1288" data-mp="1vm1588wznojq">
            {region.states.map(state => <li key={state} className="rounded border border-white/20 px-2.5 py-1 text-[13px] text-white/70" data-id="element-1289" data-mp="1qrdmkqoo1od2">
                {state}
              </li>)}
          </ul>} data-id="element-1287" data-mp="1lpy2b0ubblvq" />

      <section className="bg-sand-50 py-16 lg:py-24" aria-labelledby="region-intro-heading" data-id="element-1290" data-mp="2ef5366h8onk5">
        <div className="mx-auto grid max-w-content gap-12 px-4 sm:px-6 lg:grid-cols-12 lg:px-10" data-id="element-1291" data-mp="29finzrhuox8p">
          <div className="lg:col-span-7" data-id="element-1292" data-mp="2nes46r623rco">
            <h2 id="region-intro-heading" className="font-display text-[28px] leading-tight text-ink" data-id="element-1293" data-mp="2nftegtf909tc">
              The regional priority
            </h2>
            <p className="mt-4 text-[17px] leading-relaxed text-slate-700" data-id="element-1294" data-mp="2qizmc8mfiyzx">{region.detail}</p>

            <h3 className="mt-10 text-[12px] font-bold uppercase tracking-[0.12em] text-slate-500" data-id="element-1295" data-mp="2vgrff5yt8r5p">
              Priority commitments
            </h3>
            <ul className="mt-4 divide-y divide-sand-300 border-y border-sand-300" data-id="element-1296" data-mp="356kn3vvu0m4o">
              {region.priorities.map(priority => <li key={priority} className="flex items-start gap-3 py-4" data-id="element-1297" data-mp="3974j2w4o5jp8">
                  <span aria-hidden className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-gold-500" data-id="element-1298" data-mp="3e5pl913hedgb" />
                  <span className="text-[16px] text-slate-700" data-id="element-1299" data-mp="3j6ver14yu4qw">{priority}</span>
                </li>)}
            </ul>
          </div>

          <aside className="lg:col-span-5" data-id="element-1300" data-mp="3o2kizhrxk9tj">
            <div className="rounded-media border border-sand-200 bg-white p-7" data-id="element-1301" data-mp="1osks8zbufmoa">
              <h2 className="font-display text-[22px] text-ink" data-id="element-1302" data-mp="1tqmc3mh3fq0t">National policies behind this</h2>
              <ul className="mt-5 space-y-2" data-id="element-1303" data-mp="avj73n7ces2w">
                {relatedPolicies.map(policy => <li key={policy.slug} data-id="element-1304" data-mp="65b13fgvljkc">
                    <Link to={`/agenda/${policy.slug}`} className="group flex items-center justify-between rounded-lg border border-sand-200 px-4 py-3 transition-colors duration-200 ease-emphasis hover:border-green-200 hover:bg-sand-50" data-id="element-1305" data-mp="63hws6tqjbfi">
                      <span className="text-[15px] font-medium text-ink" data-id="element-1306" data-mp="tu3tx327bbaq">{policy.title}</span>
                      <ArrowRightIcon aria-hidden className="h-4 w-4 text-green-600 transition-transform duration-200 ease-emphasis group-hover:translate-x-1" data-id="element-1307" data-mp="ptjwdydpu5o2" />
                    </Link>
                  </li>)}
              </ul>
            </div>
          </aside>
        </div>
      </section>

      <section className="bg-white py-16 lg:py-24" aria-labelledby="region-activity-heading" data-id="element-1308" data-mp="30k1x3lzv1ssl">
        <div className="mx-auto max-w-content px-4 sm:px-6 lg:px-10" data-id="element-1309" data-mp="sc53x2zqqmxs">
          <SectionHeading eyebrow="Locally" title="Campaign activity in this region" action={<Link to="/events" className="inline-flex h-11 items-center gap-2 rounded-lg border border-ink/20 px-5 text-[15px] font-semibold text-ink transition-colors duration-150 ease-emphasis hover:border-ink/40 hover:bg-ink/[0.04]" data-id="element-1311" data-mp="jcrwefv6g8fp">
                All events
                <ArrowRightIcon aria-hidden className="h-[18px] w-[18px]" data-id="element-1312" data-mp="1g0obthevoxpc" />
              </Link>} data-id="element-1310" data-mp="x33rmnn26nb2" />

          <div className="mt-10 grid gap-8 lg:grid-cols-2" data-id="element-1313" data-mp="121yec0o5cw0g">
            <div data-id="element-1314" data-mp="1708demczsgrc">
              <h3 className="text-[12px] font-bold uppercase tracking-[0.12em] text-slate-500" data-id="element-1315" data-mp="16zq8708cqs7a">
                Upcoming events
              </h3>
              {localEvents.length > 0 ? <ul className="mt-4 space-y-4" data-id="element-1316" data-mp="3rsblr3sq96ph">
                  {localEvents.map(event => <li key={event.slug} data-id="element-1317" data-mp="3k6ohb1pjjvsv">
                      <Link to={`/events/${event.slug}`} className="group block rounded-card border border-sand-200 p-5 transition-colors duration-200 ease-emphasis hover:border-green-200 hover:bg-sand-50" data-id="element-1318" data-mp="3k9u8qnln79rr">
                        <span className="font-display text-[19px] leading-snug text-ink" data-id="element-1319" data-mp="3oae17dv9tp7k">
                          {event.title}
                        </span>
                        <span className="mt-2 flex flex-wrap gap-x-5 gap-y-1.5 text-[14px] text-slate-600" data-id="element-1320" data-mp="2wj81uzndjvjc">
                          <span className="flex items-center gap-2" data-id="element-1321" data-mp="3aav55jfqi4bm">
                            <CalendarIcon aria-hidden className="h-4 w-4 text-green-600" data-id="element-1322" data-mp="35ilajq3ocg30" />
                            {event.date}
                          </span>
                          <span className="flex items-center gap-2" data-id="element-1323" data-mp="2hrftj7lzzhr4">
                            <MapPinIcon aria-hidden className="h-4 w-4 text-green-600" data-id="element-1324" data-mp="2cto5iooh4fs7" />
                            {event.city}, {event.state}
                          </span>
                        </span>
                      </Link>
                    </li>)}
                </ul> : <p className="mt-4 rounded-card border border-dashed border-sand-300 px-5 py-8 text-[15px] text-slate-600" data-id="element-1325" data-mp="qhw4dw7ikhm3">
                  No events are scheduled in this region yet. Campaign visits are announced weekly.
                </p>}
            </div>

            <div data-id="element-1326" data-mp="2sbbollk65zm">
              <h3 className="text-[12px] font-bold uppercase tracking-[0.12em] text-slate-500" data-id="element-1327" data-mp="2rs1xs62anwo">
                Local campaign news
              </h3>
              {localNews.length > 0 ? <ul className="mt-4 space-y-4" data-id="element-1328" data-mp="btrikjd5zpz3">
                  {localNews.map(article => <li key={article.slug} data-id="element-1329" data-mp="1j58kpon2zt6y">
                      <Link to={`/news/${article.slug}`} className="group block rounded-card border border-sand-200 p-5 transition-colors duration-200 ease-emphasis hover:border-green-200 hover:bg-sand-50" data-id="element-1330" data-mp="1n73iwk9ptqgi">
                        <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-green-700" data-id="element-1331" data-mp="33q6m75wx2oi7">
                          {article.category} · {article.date}
                        </span>
                        <span className="mt-1.5 block font-display text-[18px] leading-snug text-ink" data-id="element-1332" data-mp="2tw6i97ddfpb2">
                          {article.title}
                        </span>
                      </Link>
                    </li>)}
                </ul> : <p className="mt-4 rounded-card border border-dashed border-sand-300 px-5 py-8 text-[15px] text-slate-600" data-id="element-1333" data-mp="2g0cairoc35zj">
                  No regional updates published yet. Follow the newsroom for the latest.
                </p>}
            </div>
          </div>
        </div>
      </section>

      <CtaBand title={`Organise with us in the ${region.name}.`} description="Ward coordinators, digital volunteers and professional skills are all needed locally." primary={{
      label: 'Join the movement',
      to: '/join'
    }} secondary={{
      label: 'Back to all regions',
      to: '/nigeria'
    }} data-id="element-1334" data-mp="95xoznm87x93" />
    </>;
}